import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {Bookmark, ArrowRight, AlertTriangle, Coffee, Moon, Users, Heart} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useSavedArticles } from '@/hooks/useSavedArticles'
import { lumi } from '@/lib/lumi'

const iconMap = {
  Users,
  Coffee,
  Moon,
  Heart,
  AlertTriangle
}

interface Article {
  _id: string
  title: string
  excerpt: string
  content: string
  iconType: string
  color: string
  bgColor: string
}

export default function SavedArticlesPage() {
  const { isAuthenticated } = useAuth()
  const { savedArticles, loading: savedLoading, toggleSave } = useSavedArticles()
  const [articles, setArticles] = useState<Article[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedArticle, setExpandedArticle] = useState<number | null>(null)

  useEffect(() => {
    if (!isAuthenticated || savedArticles.length === 0) {
      setArticles([])
      return
    }

    const fetchArticles = async () => {
      try {
        setLoading(true)
        const articleIds = savedArticles.map(s => s.articleId)
        const result = await lumi.entities.articles.list({
          filter: { _id: { $in: articleIds } },
          limit: 100
        })
        setArticles(result?.list || [])
      } catch (error) {
        console.error('Failed to fetch articles:', error)
        setArticles([])
      } finally {
        setLoading(false)
      }
    }

    fetchArticles()
  }, [savedArticles, isAuthenticated])

  if (!isAuthenticated) {
    return (
      <div className="bg-white min-h-screen">
        <section className="py-20">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <Bookmark className="w-16 h-16 text-[#4A90E2] mx-auto mb-6" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Sign In to Save Articles</h1>
            <p className="text-xl text-gray-600 mb-8">
              Create an account to bookmark your favorite anxiety resources and access them anytime.
            </p>
            <button
              onClick={() => lumi.auth.signInWithOAuth({ provider: 'google' })}
              className="inline-flex items-center px-8 py-4 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#357ABD] transition-colors shadow-md"
            >
              Sign In with Google
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </section>
      </div>
    )
  }

  if (loading || savedLoading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A90E2] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading saved articles...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#48C9B0] to-[#5DADE2] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Bookmark className="w-10 h-10 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Your Saved Articles</h1>
          </div>
          <p className="text-xl text-blue-100">
            {articles.length} article{articles.length !== 1 ? 's' : ''} saved for later reading
          </p>
        </div>
      </section>

      {/* Saved Articles */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {articles.length === 0 ? (
            <div className="text-center py-12">
              <Bookmark className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No saved articles yet</h3>
              <p className="text-gray-600 mb-6">Start exploring our resources and save articles you want to read later</p>
              <Link
                to="/resources"
                className="inline-flex items-center px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#357ABD] transition-colors"
              >
                Browse Resources
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          ) : (
            <div className="space-y-8">
              {articles.map((article, index) => {
                const Icon = iconMap[article.iconType as keyof typeof iconMap] || AlertTriangle
                const isExpanded = expandedArticle === index

                return (
                  <article
                    key={article._id}
                    className={`bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden transition-all ${
                      isExpanded ? 'shadow-xl' : 'hover:shadow-lg'
                    }`}
                  >
                    <div className={`${article.bgColor} p-8`}>
                      <div className="flex items-start">
                        <div className={`w-12 h-12 ${article.bgColor} rounded-lg flex items-center justify-center mr-4 flex-shrink-0 border-2 ${article.color} border-opacity-20`}>
                          <Icon className={`w-6 h-6 ${article.color}`} />
                        </div>
                        <div className="flex-grow">
                          <div className="flex items-start justify-between">
                            <div className="flex-grow pr-4">
                              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                                {article.title}
                              </h2>
                              <p className="text-lg text-gray-700 mb-4">{article.excerpt}</p>
                            </div>
                            <button
                              onClick={() => toggleSave(article._id)}
                              className="p-2 rounded-lg bg-white hover:bg-gray-100 transition-colors flex-shrink-0"
                              title="Remove from saved"
                            >
                              <Bookmark className="w-6 h-6 text-[#4A90E2] fill-[#4A90E2]" />
                            </button>
                          </div>
                          <button
                            onClick={() => setExpandedArticle(isExpanded ? null : index)}
                            className={`inline-flex items-center ${article.color} font-semibold hover:underline`}
                          >
                            {isExpanded ? 'Show Less' : 'Read Full Article'}
                            <ArrowRight className={`ml-2 w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="p-8 bg-white">
                        <div className="prose prose-lg max-w-none">
                          {article.content.split('\n\n').map((paragraph, idx) => {
                            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                              return (
                                <h3 key={idx} className="text-xl font-bold text-gray-900 mt-6 mb-3">
                                  {paragraph.replace(/\*\*/g, '')}
                                </h3>
                              )
                            } else if (paragraph.startsWith('- ')) {
                              const items = paragraph.split('\n- ').map(item => item.replace(/^- /, ''))
                              return (
                                <ul key={idx} className="list-disc list-inside space-y-2 mb-4 text-gray-700">
                                  {items.map((item, i) => (
                                    <li key={i} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                                  ))}
                                </ul>
                              )
                            } else {
                              return (
                                <p key={idx} className="text-gray-700 mb-4" dangerouslySetInnerHTML={{ 
                                  __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                                }} />
                              )
                            }
                          })}
                        </div>
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#4A90E2] to-[#5DADE2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Continue Your Journey
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Explore more resources or take our assessment
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/assessment"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-lg"
            >
              Take the GAD-7 Quiz
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/resources"
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-[#4A90E2] transition-all"
            >
              Browse All Resources
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
